import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useRegistry, ServiceDog } from '../context/RegistryContext';
import { useAuth } from '../context/AuthContext';
import {
  Search,
  Edit,
  Trash2,
  Eye,
  ChevronLeft,
  ChevronRight,
  FileDown,
  Filter,
  AlertCircle,
  RefreshCw
} from 'lucide-react';

const AdminDashboard = () => {
  const { serviceDogs, pendingDogs, deleteServiceDog, refreshDogs, loading } = useRegistry();
  const { user, isAuthenticated } = useAuth();

  const [filteredDogs, setFilteredDogs] = useState<ServiceDog[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [dogsPerPage] = useState(10);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [dogToDelete, setDogToDelete] = useState<ServiceDog | null>(null);
  const [deleteError, setDeleteError] = useState('');
  const [isLoading, setIsLoading] = useState(false);


  // Load data when component mounts or when manually refreshed
  const loadData = useCallback(async () => {
    if (!isAuthenticated || !user) {
      console.warn('User not authenticated, cannot load data');
      return;
    }

    setIsLoading(true);
    try {
      await refreshDogs();
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [refreshDogs, isAuthenticated, user]);

  // Initial data load - only once when component mounts
  // We're not using useEffect for initial load to prevent auto-refresh issues
  // The user will need to click the refresh button to load data

  // Memoize the filtered dogs to prevent unnecessary recalculations
  const filteredAndSortedDogs = useMemo(() => {
    // Check if we have any dogs to display
    if (!serviceDogs.length && !pendingDogs.length) {
      // If we're loading, return an empty array
      if (loading) {
        return [];
      }

      // If we're not loading and have no dogs, create some sample data for UI testing
      // This is just for development purposes
      const sampleDogs: ServiceDog[] = [
        {
          id: 'sample-1',
          registryNumber: 'SD-000001',
          dogName: 'Sample Dog',
          dogBreed: 'Mixed Breed',
          dogAge: '3 years',
          dogPhoto: '',
          ownerName: 'Sample Owner',
          ownerAddress: '123 Sample St',
          ownerPhone: '555-123-4567',
          ownerEmail: 'sample@example.com',
          serviceType: 'Service Dog',
          registrationDate: new Date().toISOString(),
          lastUpdated: new Date().toISOString(),
          status: 'active'
        }
      ];

      return sampleDogs;
    }

    // Combine active and pending dogs
    let allDogs = [...serviceDogs, ...pendingDogs];

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      allDogs = allDogs.filter(dog =>
        dog.dogName.toLowerCase().includes(term) ||
        dog.ownerName.toLowerCase().includes(term) ||
        dog.registryNumber.toLowerCase().includes(term)
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      allDogs = allDogs.filter(dog => dog.status === statusFilter);
    }

    // Sort by registration date (newest first)
    return allDogs.sort((a, b) =>
      new Date(b.registrationDate).getTime() - new Date(a.registrationDate).getTime()
    );
  }, [serviceDogs, pendingDogs, searchTerm, statusFilter, loading]);

  // Update filtered dogs when the memoized value changes
  useEffect(() => {
    setFilteredDogs(filteredAndSortedDogs);
    setCurrentPage(1); // Reset to first page when filters change
  }, [filteredAndSortedDogs]);

  // Get current dogs for pagination
  const indexOfLastDog = currentPage * dogsPerPage;
  const indexOfFirstDog = indexOfLastDog - dogsPerPage;
  const currentDogs = filteredDogs.slice(indexOfFirstDog, indexOfLastDog);
  const totalPages = Math.ceil(filteredDogs.length / dogsPerPage);

  // Change page
  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);
  const nextPage = () => setCurrentPage(prev => Math.min(prev + 1, totalPages));
  const prevPage = () => setCurrentPage(prev => Math.max(prev - 1, 1));

  // Open delete confirmation modal
  const openDeleteModal = (dog: ServiceDog) => {
    setDogToDelete(dog);
    setIsDeleteModalOpen(true);
    setDeleteError('');
  };

  // Close delete confirmation modal
  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setDogToDelete(null);
  };

  // Handle delete confirmation
  const confirmDelete = async () => {
    if (!dogToDelete) return;

    // Check authentication
    if (!isAuthenticated || !user) {
      setDeleteError('You must be logged in as an admin to delete records. Please log in again.');
      return;
    }

    setIsLoading(true);
    try {
      console.log(`Attempting to delete dog with ID: ${dogToDelete.id}, Status: ${dogToDelete.status}`);

      // Actually delete the dog from the database
      await deleteServiceDog(dogToDelete.id, dogToDelete.status);

      // Close the modal after successful deletion
      closeDeleteModal();

      // Refresh the data to ensure our UI is up to date
      await loadData();

    } catch (error: any) {
      console.error('Error deleting dog:', error);

      // Provide more specific error messages
      if (error.code === 'permission-denied') {
        setDeleteError('Permission denied. Make sure you are logged in as an admin user and have the proper permissions.');
      } else if (error.message && error.message.includes('permission')) {
        setDeleteError('Permission error: ' + error.message);
      } else {
        setDeleteError(`An error occurred while deleting: ${error.message || 'Unknown error'}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Export data as CSV
  const exportCSV = () => {
    // Column headers
    const headers = [
      'Registry Number',
      'Dog Name',
      'Dog Breed',
      'Dog Age',
      'Service Type',
      'Owner Name',
      'Owner Email',
      'Owner Phone',
      'Registration Date',
      'Status'
    ];

    // Data rows
    const rows = filteredDogs.map(dog => [
      dog.registryNumber,
      dog.dogName,
      dog.dogBreed,
      dog.dogAge,
      dog.serviceType,
      dog.ownerName,
      dog.ownerEmail,
      dog.ownerPhone,
      new Date(dog.registrationDate).toLocaleDateString(),
      dog.status
    ]);

    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `service-dog-registry-${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="animate-fade-in">
      <div className="bg-white rounded-lg shadow-md overflow-hidden mb-4 sm:mb-6">
        <div className="bg-primary-600 text-white p-4 sm:p-6">
          <h1 className="text-xl sm:text-2xl font-bold">Admin Dashboard</h1>
          <p className="text-primary-100 mt-1 sm:mt-2 text-sm sm:text-base">
            Manage and maintain the service dog registry database.
          </p>
          {(loading || isLoading) && (
            <div className="mt-2 bg-white bg-opacity-20 text-white px-2 sm:px-3 py-1 rounded-full inline-flex items-center text-xs sm:text-sm">
              <svg className="animate-spin -ml-1 mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Loading...
            </div>
          )}
        </div>

        {/* Welcome message - show when first loading the dashboard */}
        {isAuthenticated && user && !loading && !isLoading && filteredDogs.length === 0 && (
          <div className="bg-primary-100 border-l-4 border-primary-500 text-primary-700 p-3 sm:p-4">
            <div className="flex flex-col sm:flex-row sm:items-start">
              <RefreshCw className="h-5 w-5 mx-auto sm:mx-0 sm:mr-2 sm:mt-0.5 mb-2 sm:mb-0 flex-shrink-0" />
              <div className="text-center sm:text-left">
                <p className="font-bold text-sm sm:text-base">Welcome to the Admin Dashboard</p>
                <p className="text-xs sm:text-sm mt-1">
                  Click the Refresh button to load the dog registry data. Auto-refresh has been disabled to prevent performance issues.
                </p>
                <div className="mt-2">
                  <button
                    onClick={loadData}
                    disabled={isLoading}
                    className="bg-primary-600 hover:bg-primary-700 text-white px-3 py-1 rounded text-xs sm:text-sm"
                  >
                    Load Data
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="p-3 sm:p-6">
          {/* Search and Filter Controls */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 space-y-3 sm:space-y-0">
            <div className="relative flex-grow sm:max-w-md">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search size={16} className="text-neutral-400" />
              </div>
              <input
                type="text"
                placeholder="Search by name or registry number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 w-full px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <div className="flex flex-wrap gap-2 sm:gap-0 sm:space-x-2 md:space-x-4">
              <div className="flex items-center">
                <Filter size={16} className="text-neutral-500 mr-1 sm:mr-2" />
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-2 sm:px-3 py-2 text-xs sm:text-sm border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="all">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="pending">Pending</option>
                  <option value="revoked">Revoked</option>
                </select>
              </div>

              <button
                onClick={() => loadData()}
                disabled={isLoading}
                className="flex items-center bg-success-600 hover:bg-success-700 text-white px-2 sm:px-3 py-2 rounded-lg shadow transition-colors disabled:opacity-50 text-xs sm:text-sm"
                title="Load registry data"
              >
                <RefreshCw size={16} className={`mr-1 sm:mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                <span className="hidden xs:inline">Load Registry</span>
                <span className="xs:hidden">Load</span>
              </button>

              <button
                onClick={exportCSV}
                className="flex items-center bg-primary-600 hover:bg-primary-700 text-white px-2 sm:px-3 py-2 rounded-lg shadow transition-colors text-xs sm:text-sm"
              >
                <FileDown size={16} className="mr-1 sm:mr-2" />
                <span className="hidden xs:inline">Export CSV</span>
                <span className="xs:hidden">Export</span>
              </button>
            </div>
          </div>

          {/* Data Table */}
          <div className="overflow-x-auto -mx-3 sm:mx-0">
            <table className="min-w-full divide-y divide-neutral-200 text-xs sm:text-sm">
              <thead className="bg-neutral-50">
                <tr>
                  <th scope="col" className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Registry #
                  </th>
                  <th scope="col" className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Dog Name
                  </th>
                  <th scope="col" className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider hidden sm:table-cell">
                    Owner
                  </th>
                  <th scope="col" className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider hidden md:table-cell">
                    Service Type
                  </th>
                  <th scope="col" className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider hidden sm:table-cell">
                    Reg. Date
                  </th>
                  <th scope="col" className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="px-3 sm:px-6 py-2 sm:py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-neutral-200">
                {currentDogs.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-3 sm:px-6 py-8 sm:py-12 text-center text-neutral-500">
                      {searchTerm || statusFilter !== 'all' ? (
                        <div>
                          <AlertCircle size={24} className="mx-auto mb-2 sm:mb-3 text-neutral-400" />
                          <p className="text-xs sm:text-sm">No service dogs match your search criteria.</p>
                          <button
                            onClick={() => {
                              setSearchTerm('');
                              setStatusFilter('all');
                            }}
                            className="mt-2 text-primary-600 hover:text-primary-700 underline text-xs sm:text-sm"
                          >
                            Clear filters
                          </button>
                        </div>
                      ) : (
                        <p className="text-xs sm:text-sm">No service dogs have been registered yet.</p>
                      )}
                    </td>
                  </tr>
                ) : (
                  currentDogs.map((dog) => (
                    <tr key={dog.id} className="hover:bg-neutral-50">
                      <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                        <span className="font-mono text-xs sm:text-sm text-neutral-900">{dog.registryNumber}</span>
                      </td>
                      <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-neutral-900">
                        {dog.dogName}
                      </td>
                      <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-neutral-900 hidden sm:table-cell">
                        {dog.ownerName}
                      </td>
                      <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-neutral-900 hidden md:table-cell">
                        {dog.serviceType}
                      </td>
                      <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-neutral-600 hidden sm:table-cell">
                        {new Date(dog.registrationDate).toLocaleDateString()}
                      </td>
                      <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-1.5 sm:px-2.5 py-0.5 rounded-full text-xs font-medium
                            ${dog.status === 'active' ? 'bg-success-100 text-success-800' : ''}
                            ${dog.status === 'pending' ? 'bg-warning-100 text-warning-800' : ''}
                            ${dog.status === 'revoked' ? 'bg-error-100 text-error-800' : ''}
                          `}
                        >
                          {dog.status.charAt(0).toUpperCase() + dog.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-right text-xs sm:text-sm font-medium">
                        <div className="flex justify-end space-x-2 sm:space-x-3">
                          <Link
                            to={`/registry/${dog.id}`}
                            className="text-primary-600 hover:text-primary-900"
                            title="View registry card"
                          >
                            <Eye size={16} className="sm:h-[18px] sm:w-[18px]" />
                          </Link>
                          <Link
                            to={`/admin/edit/${dog.id}`}
                            className="text-neutral-600 hover:text-neutral-900"
                            title="Edit record"
                          >
                            <Edit size={16} className="sm:h-[18px] sm:w-[18px]" />
                          </Link>
                          <button
                            onClick={() => openDeleteModal(dog)}
                            className="text-accent-600 hover:text-accent-900"
                            title="Delete record"
                          >
                            <Trash2 size={16} className="sm:h-[18px] sm:w-[18px]" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mt-4 sm:mt-6 space-y-2 sm:space-y-0">
              <div className="text-xs sm:text-sm text-neutral-600 text-center sm:text-left">
                Showing {indexOfFirstDog + 1}-{Math.min(indexOfLastDog, filteredDogs.length)} of {filteredDogs.length} records
              </div>
              <div className="flex items-center justify-center sm:justify-end space-x-1 sm:space-x-2">
                <button
                  onClick={prevPage}
                  disabled={currentPage === 1}
                  className="p-1 sm:p-2 rounded-md border border-neutral-300 bg-white text-neutral-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft size={14} className="sm:h-4 sm:w-4" />
                </button>

                {/* Page numbers */}
                <div className="flex space-x-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(num =>
                      num === 1 ||
                      num === totalPages ||
                      Math.abs(num - currentPage) <= 1
                    )
                    .map((number, index, array) => {
                      // Add ellipsis
                      if (index > 0 && array[index - 1] !== number - 1) {
                        return (
                          <React.Fragment key={`ellipsis-${number}`}>
                            <span className="px-1 sm:px-2 py-1 text-neutral-400 text-xs sm:text-sm">...</span>
                            <button
                              key={number}
                              onClick={() => paginate(number)}
                              className={`w-6 h-6 sm:w-8 sm:h-8 rounded-md text-xs sm:text-sm ${
                                currentPage === number
                                  ? 'bg-primary-600 text-white'
                                  : 'bg-white border border-neutral-300 text-neutral-700 hover:bg-neutral-50'
                              }`}
                            >
                              {number}
                            </button>
                          </React.Fragment>
                        );
                      }

                      return (
                        <button
                          key={number}
                          onClick={() => paginate(number)}
                          className={`w-6 h-6 sm:w-8 sm:h-8 rounded-md text-xs sm:text-sm ${
                            currentPage === number
                              ? 'bg-primary-600 text-white'
                              : 'bg-white border border-neutral-300 text-neutral-700 hover:bg-neutral-50'
                          }`}
                        >
                          {number}
                        </button>
                      );
                    })
                  }
                </div>

                <button
                  onClick={nextPage}
                  disabled={currentPage === totalPages}
                  className="p-1 sm:p-2 rounded-md border border-neutral-300 bg-white text-neutral-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight size={14} className="sm:h-4 sm:w-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full animate-fade-in">
            <div className="p-4 sm:p-6">
              <h2 className="text-lg sm:text-xl font-bold text-neutral-900 mb-3 sm:mb-4">Confirm Deletion</h2>

              {deleteError && (
                <div className="mb-3 sm:mb-4 p-2 sm:p-3 bg-accent-100 text-accent-700 rounded-lg flex items-start text-xs sm:text-sm">
                  <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5 mr-2 mt-0.5 flex-shrink-0" />
                  <span>{deleteError}</span>
                </div>
              )}

              <p className="text-neutral-600 mb-4 sm:mb-6 text-sm sm:text-base">
                Are you sure you want to delete the registry record for <span className="font-semibold">{dogToDelete?.dogName}</span> ({dogToDelete?.registryNumber})? This action cannot be undone.
              </p>

              <div className="flex justify-end space-x-3 sm:space-x-4">
                <button
                  onClick={closeDeleteModal}
                  className="px-3 sm:px-4 py-1.5 sm:py-2 border border-neutral-300 text-neutral-700 rounded-md hover:bg-neutral-50 transition-colors text-xs sm:text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="px-3 sm:px-4 py-1.5 sm:py-2 bg-accent-600 text-white rounded-md hover:bg-accent-700 transition-colors text-xs sm:text-sm"
                >
                  Delete Record
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
